// Restaurant registration -> Pending -> Super Admin approval -> Restaurant ID -> activation -> setup.
import { useState, type FormEvent } from 'react';
import { ApiError, request, setSession } from '../api';
import brand from '../brand.generated.json';
import { dateTime, Loadable, useLoad } from '../ui';

export type Registration = {
  id: string;
  reference: string;
  restaurant_name: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'BLOCKED';
  submitted_at: string;
  decided_at: string | null;
  rejection_reason: string | null;
  blocked_reason: string | null;
  restaurant: { id: string; restaurant_id: string; status: string; approved_at: string } | null;
};

type Policy = { country_mode: string; allowed_countries: string[]; allowed_languages: string[] };

const COUNTRY_NAMES: Record<string, string> = { IN: 'India', NP: 'Nepal', BD: 'Bangladesh', LK: 'Sri Lanka', AE: 'United Arab Emirates', US: 'United States', GB: 'United Kingdom' };
const BUSINESS_TYPES = [
  ['restaurant', 'Restaurant'], ['cafe', 'Cafe'], ['qsr', 'Quick-service restaurant'], ['bakery', 'Bakery'], ['cloud_kitchen', 'Cloud kitchen'], ['bar', 'Bar'], ['other', 'Other'],
];

const EMPTY = { restaurant_name: '', owner_name: '', phone: '', address: '', city: '', state: '', postal_code: '', country: 'IN', business_type: 'restaurant', notes: '' };

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="field">
      {label}
      {children}
      {hint && <span className="small muted">{hint}</span>}
    </label>
  );
}

function RestaurantFields({ f, set, policy }: { f: typeof EMPTY; set: (f: typeof EMPTY) => void; policy: Policy | null }) {
  const countries = policy?.allowed_countries ?? ['IN'];
  return (
    <div className="form-grid">
      <Field label="Restaurant name"><input required minLength={2} value={f.restaurant_name} onChange={(e) => set({ ...f, restaurant_name: e.target.value })} /></Field>
      <Field label="Business type"><select value={f.business_type} onChange={(e) => set({ ...f, business_type: e.target.value })}>{BUSINESS_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
      <Field label="Owner name"><input required autoComplete="name" value={f.owner_name} onChange={(e) => set({ ...f, owner_name: e.target.value })} /></Field>
      <Field label="Phone number"><input required type="tel" autoComplete="tel" pattern="[+0-9 ()-]{6,30}" value={f.phone} onChange={(e) => set({ ...f, phone: e.target.value })} placeholder="+91 mobile number" /></Field>
      <Field label="Address"><input required minLength={3} autoComplete="street-address" value={f.address} onChange={(e) => set({ ...f, address: e.target.value })} /></Field>
      <Field label="City"><input required autoComplete="address-level2" value={f.city} onChange={(e) => set({ ...f, city: e.target.value })} /></Field>
      <Field label="State"><input autoComplete="address-level1" value={f.state} onChange={(e) => set({ ...f, state: e.target.value })} /></Field>
      <Field label="PIN / postal code"><input autoComplete="postal-code" value={f.postal_code} onChange={(e) => set({ ...f, postal_code: e.target.value })} /></Field>
      <Field label="Country" hint={policy?.country_mode === 'india_only' ? `${brand.brandName} currently accepts restaurants in India.` : undefined}>
        <select value={f.country} onChange={(e) => set({ ...f, country: e.target.value })}>{countries.map((c) => <option key={c} value={c}>{COUNTRY_NAMES[c] ?? c}</option>)}</select>
      </Field>
    </div>
  );
}

function usePolicy() {
  return useLoad<Policy>(() => request('GET', '/v1/platform/registration-policy', undefined, false), []);
}

/** Public sign-up: creates the owner account and the PENDING registration together. */
export function RestaurantSignup({ go }: { go: (p: string) => void }) {
  const policy = usePolicy();
  const [f, setF] = useState(EMPTY);
  const [account, setAccount] = useState({ email: '', password: '', accept_terms: false });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const data = await request<{ access_token: string }>('POST', '/v1/registrations/signup', { ...f, ...account, client: 'web' }, false);
      setSession(data.access_token);
      go('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="auth">
      <div className="auth-card wide">
        <div className="brand-mark">{brand.brandName}</div>
        <h1>Register your restaurant</h1>
        <p className="muted">Registration is reviewed by the {brand.brandName} team. You will get your permanent Restaurant ID once it is approved.</p>
        <form onSubmit={submit} className="stack" aria-label="Restaurant registration">
          <RestaurantFields f={f} set={setF} policy={policy.data} />
          <div className="form-grid">
            <Field label="Email (sign-in)"><input required type="email" autoComplete="email" value={account.email} onChange={(e) => setAccount({ ...account, email: e.target.value })} /></Field>
            <Field label="Password" hint="At least 10 characters."><input required type="password" minLength={10} autoComplete="new-password" value={account.password} onChange={(e) => setAccount({ ...account, password: e.target.value })} /></Field>
          </div>
          <label className="check"><input type="checkbox" required checked={account.accept_terms} onChange={(e) => setAccount({ ...account, accept_terms: e.target.checked })} /> I accept the terms of service and privacy policy.</label>
          {error && <div className="alert error" role="alert">{error}</div>}
          <button className="btn" disabled={busy}>Submit registration</button>
        </form>
        <p className="muted">Already registered? <a href="#/login">Sign in</a></p>
      </div>
    </div>
  );
}

/** Signed-in owner registering another outlet. Each outlet gets its own single approval. */
export function NewOutlet({ onDone }: { onDone: () => void }) {
  const policy = usePolicy();
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <section className="panel">
      <h1>Register an outlet</h1>
      <p className="muted">Each outlet is a separate restaurant with its own approval, Restaurant ID and modules. Your approved outlets keep working while this one is reviewed.</p>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await request('POST', '/v1/registrations', f);
            onDone();
          } catch (err) {
            setError(err instanceof ApiError ? err.message : String(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <RestaurantFields f={f} set={setF} policy={policy.data} />
        {error && <div className="alert error" role="alert">{error}</div>}
        <button className="btn" disabled={busy}>Submit registration</button>
      </form>
    </section>
  );
}

export function RegistrationCard({ r, go, onRefresh }: { r: Registration; go: (p: string) => void; onRefresh: () => void }) {
  if (r.status === 'PENDING') {
    return (
      <section className="panel status-card pending" data-testid="registration-pending">
        <span className="pill warning">Pending approval</span>
        <h1>Registration Submitted Successfully</h1>
        <p>Your restaurant registration is pending approval from the {brand.brandName} administration team. You will be able to access your restaurant dashboard once your registration is approved.</p>
        <dl className="facts">
          <dt>Restaurant</dt><dd>{r.restaurant_name}</dd>
          <dt>Registration reference</dt><dd><code>{r.reference}</code></dd>
          <dt>Registration date</dt><dd>{dateTime(r.submitted_at)}</dd>
          <dt>Status</dt><dd>Pending Approval</dd>
        </dl>
        <button className="btn secondary" onClick={onRefresh}>Refresh status</button>
      </section>
    );
  }
  if (r.status === 'REJECTED') {
    return (
      <section className="panel status-card rejected" data-testid="registration-rejected">
        <span className="pill critical">Not approved</span>
        <h1>Registration not approved</h1>
        <dl className="facts">
          <dt>Restaurant</dt><dd>{r.restaurant_name}</dd>
          <dt>Registration reference</dt><dd><code>{r.reference}</code></dd>
          <dt>Reason</dt><dd>{r.rejection_reason ?? 'n/a'}</dd>
        </dl>
        <p className="muted">You can contact {brand.supportEmail || 'support'} or submit a corrected registration. A new submission is reviewed once, like the first.</p>
        <a className="btn secondary" href="#/register-outlet">Submit a corrected registration</a>
      </section>
    );
  }
  if (r.status === 'BLOCKED') {
    return (
      <section className="panel status-card blocked" data-testid="restaurant-blocked">
        <span className="pill critical">Blocked</span>
        <h1>{r.restaurant_name} is blocked</h1>
        <p>{brand.brandName} administration has blocked this restaurant. All your data, orders and payments are preserved. Contact support to restore access.</p>
        <dl className="facts">
          <dt>Restaurant ID</dt><dd><code>{r.restaurant?.restaurant_id}</code></dd>
          <dt>Reason</dt><dd>{r.blocked_reason ?? 'n/a'}</dd>
        </dl>
      </section>
    );
  }
  return (
    <section className="panel status-card approved" data-testid="registration-approved">
      <span className="pill good">Approved</span>
      <h1>Your Restaurant Has Been Approved!</h1>
      <p>Welcome to {brand.brandName}. Your restaurant account is now active and ready for setup.</p>
      <dl className="facts">
        <dt>Restaurant</dt><dd>{r.restaurant_name}</dd>
        <dt>Restaurant ID</dt><dd><code className="big-code" data-testid="restaurant-id">{r.restaurant?.restaurant_id}</code></dd>
        <dt>Approval status</dt><dd>Approved {dateTime(r.decided_at)}</dd>
      </dl>
      <button className="btn" onClick={() => go(`/stores/${r.restaurant?.id}/setup`)}>Continue Setup</button>
    </section>
  );
}

export function Registrations({ go }: { go: (p: string) => void }) {
  const state = useLoad<{ registrations: Registration[] }>(() => request('GET', '/v1/registrations'), []);
  return (
    <Loadable state={state}>
      {(d) => (
        <div className="stack">
          {d.registrations.length === 0 && (
            <section className="panel narrow">
              <h1>Register your restaurant</h1>
              <p>Tell us about your restaurant to request access to {brand.productName}.</p>
              <a className="btn" href="#/register-outlet">Register restaurant</a>
            </section>
          )}
          {d.registrations.map((r) => <RegistrationCard key={r.id} r={r} go={go} onRefresh={state.reload} />)}
        </div>
      )}
    </Loadable>
  );
}

type Features = { status: string | null; restaurant_id: string | null; features: Record<string, boolean>; catalog: Array<{ key: string; group: string; label: string; description: string }> };

export function ActivatePos({ storeId, onActivated }: { storeId: string; onActivated?: () => void }) {
  const [code, setCode] = useState('');
  const [replace, setReplace] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [needsReplace, setNeedsReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <section className="panel" aria-label="Activate POS">
      <h3>Activate a POS</h3>
      <ol className="steps">
        <li>Install {brand.productName} on the restaurant computer and finish its first-run setup.</li>
        <li>The POS shows an <strong>activation code</strong> until it is activated.</li>
        <li>Enter that code here. Codes expire after 10 minutes.</li>
      </ol>
      <div className="form-grid">
        <label className="field">Activation code<input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABCD2345" autoCapitalize="characters" /></label>
      </div>
      {needsReplace && <label className="check"><input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} /> Replace the POS that is currently active for this restaurant (it will be disconnected; its data stays on that computer).</label>}
      <button
        className="btn"
        disabled={busy || code.length < 4}
        onClick={async () => {
          setBusy(true);
          setMsg(null);
          try {
            await request('POST', '/v1/pairing/redeem', { code, restaurant_id: storeId, replace_existing: replace, device_name: 'Web dashboard', platform: 'web' });
            setMsg({ kind: 'ok', text: 'POS activated. It unlocks within a few seconds.' });
            setCode('');
            onActivated?.();
          } catch (err) {
            if (err instanceof ApiError && err.code === 'restaurant_has_pos') setNeedsReplace(true);
            setMsg({ kind: 'error', text: err instanceof ApiError ? err.message : String(err) });
          } finally {
            setBusy(false);
          }
        }}
      >
        Activate POS
      </button>
      {msg && <div className={`alert ${msg.kind}`} role="status">{msg.text}</div>}
    </section>
  );
}

export function Modules({ storeId }: { storeId: string }) {
  const state = useLoad<Features>(() => request('GET', `/v1/stores/${storeId}/features`), [storeId]);
  return (
    <Loadable state={state}>
      {(d) => (
        <section className="panel">
          <h3>Modules for this restaurant</h3>
          <p className="muted small">Modules are enabled by {brand.controlName}. Contact support to change them.</p>
          <ul className="module-list">
            {d.catalog.map((f) => (
              <li key={f.key}>
                <span className={`pill ${d.features[f.key] ? 'good' : ''}`}>{d.features[f.key] ? 'Enabled' : 'Locked'}</span>
                <strong>{f.label}</strong>
                <span className="muted small">: {f.description}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Loadable>
  );
}

export function RestaurantSetup({ store }: { store: { id: string; name: string; restaurant_id?: string | null; pos_devices?: number } }) {
  return (
    <div className="stack" data-testid="restaurant-setup">
      <section className="panel">
        <h3>Set up {store.name}</h3>
        <p>Restaurant ID <code className="big-code">{store.restaurant_id}</code></p>
        <ol className="steps">
          <li><a href={brand.urls.website ? `${brand.urls.website.replace(/\/+$/, '')}/download` : '#/'}>Download {brand.productName}</a> for Windows, macOS or Linux and install it.</li>
          <li>Finish the POS first-run setup (owner account, business details, tax).</li>
          <li>Activate the POS below with the code it shows.</li>
          <li>In the POS: add tables, menu, staff, printers and payment methods; connect WhatsApp by scanning the QR code on the POS WhatsApp page, and set up loyalty if enabled.</li>
          <li>Install {brand.ownerAppName} on your phone to follow sales.</li>
        </ol>
      </section>
      <ActivatePos storeId={store.id} />
      <Modules storeId={store.id} />
    </div>
  );
}

